import { ChangeDetectorRef, Component, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { SeConstraintNotifierService } from '@se/se.platform.base';
import { IDataSourceItem } from '@se/cp.common';
import { IAnatomicMap, IColorCode, IColorData, IContainerConfig, IFixedSize, IGuid, ImageType, MapContainerComponent, RasterImgWrapper } from '@se/ui.anatomic.map';
import { DataBinding, Guid, Logger } from '@se/se.platform.base';
import { firstValueFrom, forkJoin, of, Subscription, takeUntil } from 'rxjs';

import { ICpEntity, PropertyType } from '../../cp-configuration/view-model';
import { CpMapContainerControl } from '../../cp-configuration/view-model/cp-map-container-control';
import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';
import { CpEffects, EffectResultImage, EffectResultImageDelete, EventResult, ObservableOrAwaitable } from '../../store/cp.effects';
import { AbstractControlComponent } from '../abstract-control';
import { CpMapParsers } from './cp-parsers.service';
import { MapLoaderService } from './mapLoader.service';
import { ICpDescription } from './models/ICpDescription';

@Component({
    selector: 'cp-map-wrapper',
    templateUrl: './map-wrapper.component.html',
    styleUrls: ['./map-wrapper.component.scss'],
    standalone: false
})
export class CpMapWrapperComponent extends AbstractControlComponent implements OnDestroy {
    control!: CpMapContainerControl;

    public containerConfig: IContainerConfig;
    public colorData: IColorData[] = [];
    public colorLegend: IColorCode[] = [];
    public fixedSize: IFixedSize | null = null;
    public isLocalCPEntityToPrint: boolean  | null = null;

    public get isLoading() {
        return this._isLoading;
    }
    @ViewChild(MapContainerComponent, { static: false })
    public containerComponent!: MapContainerComponent;

    @HostBinding('style')
    get dimensions() {
        return {
            height: this._viewsSideLenght + 'px'
        }
    }

    @HostBinding('class.map-wrapper-hidden')
    get _isMapHidden(): boolean{ return !!this.isHidden; };

    public _viewsSideLenght: number = 0;
    public _viewTopLabel: string | null = null;
    public _viewBottomLabel: string | null = null;

    private _isLoading = false;
    private containerSub!: Subscription;
    private localCpEntity!: ICpEntity;
    private initialLoad = true;
    private descriptions: ICpDescription[] = [];
    private currentViewSet: string = '';
    private guids: IGuid[] = [];
    private guidSToDelete: IGuid[] = [];

    constructor(
        private cpEffects: CpEffects,
        private dataSourceService: CpDatasourceService,
        private modelService: CpModelService,
        private mapLoaderService: MapLoaderService,
        private parsers: CpMapParsers,
        cdr: ChangeDetectorRef,
        constraintNotifier: SeConstraintNotifierService,
        logger: Logger,
    ) {
        super(cdr, constraintNotifier, logger);
        this.containerConfig = this.createContainer('', []);
    }

    // requested by parent abstract class
    protected onModelChange(): boolean {
        return true;
    }

    protected handleHiddenNotification(isHidden : boolean){
        this.isHidden = isHidden;
    }

    _isMapDisabled: boolean = false;
    set isMapDisabled(value: boolean){
        this._isMapDisabled = value;
    }
    get isMapDisabled(){
        return this._isMapDisabled;
    }

    protected handleDisabledNotification(isDisabled : boolean){
            this.isMapDisabled = isDisabled || this.isDisabled;
    }

    protected async init() {
        this._isLoading = true;
        this._viewsSideLenght = this.control.getHeightHint();

        this.initColorLegend();
        this.initDescriptions();
        this.setViewLabels();
        this.registerDataBinding();
        this.registerLabelDataBinding()
        this.initImgSaving();
    }

    private initImgSaving() {
        if (this.containerComponent && (this.control.viewsImageBinding || this.control.legendImageBinding)) {
            this.cpEffects.on('collectImages', this.saveHandlerWrapper.bind(this));
            this.setIsToPrint();
        }
    }

    private initDescriptions() {
        if (this.control?.segmentsDataSource) {
            this.descriptions = this.dataSourceService
                .getById(this.control.segmentsDataSource)
                ?.map((desc) => this.parsers.parseRegionDescription(desc)) || [];
        }
    }

    private initColorLegend() {
        this.colorLegend = this.dataSourceService
            .getById(this.control.legendDataSource)
            ?.map((item) => this.parsers.parseColor(item)) || [];
    }

    private registerDataBinding() {
        this.modelService
            .registerEntity(this.control.dataBinding || new DataBinding(), true)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(async (entity) => {
                if (!entity) {
                    // gestire il caso vuoto
                    return;
                }

                if (this.initialLoad) {
                    this.initialLoad = false;
                    this.onEntityFirstLoad(entity);
                    const views: IAnatomicMap[] = await this.initViews();
                    if (!!views) {
                        this.registerViewSetBindingChange();
                    }
                    this.containerConfig = this.createContainer(this.control.name, views);
                } else {
                    this.onEntityUpdate(entity);
                }
            });
    }

    private registerViewSetBindingChange() {
        this.modelService
            .register(this.control.viewSetBinding || new DataBinding())
            .pipe(takeUntil(this.destroyed$))
            .subscribe(async (viewSetBinding: PropertyType | undefined) => {
                if (this.isViewSetBidingChanged(viewSetBinding)) {
                    const views: IAnatomicMap[] = await this.initViews();
                    this.containerConfig = this.createContainer(this.control.name, views);
                }
            });
    }

    /**
     * Gestisce la casistica in cui currentView è la stringa 'default'
     * @param viewSetBinding
     * @returns
     */
    private isViewSetBidingChanged(viewSetBinding: PropertyType | undefined): boolean {
        if(viewSetBinding == null) {
            return true;
        }
        return viewSetBinding.toString() !== this.currentViewSet;
    }

    // TODO controllare sia !!this.control.mapViews che !!this.control.viewsSets
    private async initViews(): Promise<IAnatomicMap[]> {
        let viewsPromise: Promise<IAnatomicMap[]>;
            this.currentViewSet = 'default';
            if (this.control.viewSetBinding) {
                this.currentViewSet = this.bool2label(this.modelService.getValue(this.control.viewSetBinding) as boolean | string);
            }
            // const mapLabel = this.retriveMapLabels(this.localCpEntity);
            viewsPromise = firstValueFrom(forkJoin(
                (this.control.viewsSets![this.currentViewSet as string] ?? this.control.viewsSets!['default']).map((mapName) => {
                    return this.mapLoaderService.load(mapName)
                })
            ));
        return viewsPromise;
    }

    private setViewLabels(){
        // if(!isDataBindingProperty(this.control.viewTopLabel) && typeof this.control.viewTopLabel === 'string'){
            this._viewTopLabel = this.control.viewTopLabel;
        // }
        // if(!isDataBindingProperty(this.control.viewBottomLabel) && typeof this.control.viewBottomLabel === 'string'){
            this._viewBottomLabel = this.control.viewBottomLabel;
        // }
    }

    private registerLabelDataBinding(){
        if(this.control.viewTopLabelBinding) {
            this.modelService.register(this.control.viewTopLabelBinding)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((viewTopLabel: PropertyType | undefined) => {
                if (!viewTopLabel) {
                    return;
                }
            this._viewTopLabel = viewTopLabel as string;
            })
        }
        if(this.control.viewBottomLabelBinding) {
            this.modelService.register(this.control.viewBottomLabelBinding)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((viewBottomLabel:  PropertyType | undefined) => {
                if (!viewBottomLabel) {
                    return;
                }
            this._viewBottomLabel = viewBottomLabel as string;
            })
        }
    }

    private createContainer(title: string, maps: IAnatomicMap[]): IContainerConfig {
          return {
            id: title,
            name: title,
            maps,
        };
    }

    private onEntityFirstLoad(newEntity: ICpEntity) {
        this._isLoading = true;
        this.saveLocalData(newEntity);
        this._isLoading = false;
        this.cdr.detectChanges();
        this.subscribeToContainer();
    }

    private onEntityUpdate(newEntity: ICpEntity) {
        this.saveLocalData(newEntity);
    }

    private saveLocalData(newEntity: ICpEntity) {
        const fallbackColorId = this.colorLegend[0].colorID;
        this.localCpEntity = newEntity;
        this.colorData = this.parsers.parseRegionsValue(newEntity, this.descriptions, this.colorLegend, fallbackColorId);
    }

    private subscribeToContainer() {
        this.containerSub = this.containerComponent.dataUpdate.subscribe((data) => this.onDataUpdate(data));
    }

    private onDataUpdate(newData: IColorData[]): void {
        const legendDatasource: IDataSourceItem[] | undefined = this.dataSourceService.getById(this.control.legendDataSource!);
        newData.forEach((colorData: IColorData) => {
            const legendItemForUpdate: IDataSourceItem | undefined = legendDatasource?.find(legendItem => legendItem.Id === colorData.colorID);
            const newEntity: ICpEntity = this.modelService.makeEntity(legendItemForUpdate as any, this.localCpEntity.path);
            this.localCpEntity.setPropertyValue(colorData.regionID, newEntity)
        });
        const isNewDataUpdated = this.modelService.setValue(
            this.control.dataBinding!,
            this.localCpEntity,
            {force: true}
        );
        if (newData.length > 0 && isNewDataUpdated) {
            this.edited.emit();
        }
    }

    private haveDataChanged(newData: IColorData[], oldData: IColorData[]): boolean {
        return !newData.every((newD, i) => oldData[i].colorID === newD.colorID);
    }

    private bool2label(val: boolean | string): string {
        if(val == null) return "default";
        return val.toString();
    }

    private ensureMapGuid(propertyValue: PropertyType): string | undefined {
        // se c'è un guid uso quello
        if (!!propertyValue) {
            return propertyValue.toString();
        }
        // se è vuoto creo un nuovo Guid
        //genera Guid
        const imgGuid = new Guid().toString();

        this.setDataBindingGuid(imgGuid,this.control.viewsImageBinding!);
        return imgGuid;
    }

    private setDataBindingGuid(imgGuid: string | null, binding: DataBinding) {
        this.modelService.setValue(
            binding,
            imgGuid
        );
    }

    private getMapBindingPropertyValue(): PropertyType | null {
            const cpEntity: ICpEntity | undefined = this.modelService.getEntity(this.control.viewsImageBinding!.entity);
            const propertyValue: PropertyType | null = cpEntity ? cpEntity.getPropertyValue(this.control.viewsImageBinding?.propertyName!) : null;
            return propertyValue;
    }

    private getlegendImageBindingPropertyValue(): PropertyType | null {
            const cpEntity: ICpEntity | undefined = this.modelService.getEntity(this.control.legendImageBinding?.entity!);
            const propertyValue: PropertyType | null = cpEntity ? cpEntity.getPropertyValue(this.control.legendImageBinding?.propertyName!) : null;
            return propertyValue;
    }

    private ensureLegendGuid(propertyValue: PropertyType): string | undefined {
        if (!!propertyValue) {
            return propertyValue.toString();
        }
        // se è vuoto creo un nuovo Guid
            //genera Guid
        const imgGuid = new Guid().toString();
        this.setDataBindingGuid(imgGuid, this.control.legendImageBinding!);
        return imgGuid;
    }

    private beforeSave() {
        this.guids = [];
        this.guidSToDelete = [];
        let mapGuid = null;
        let legendGuid = null;

        this.setIsToPrint();
        if(!this.isLocalCPEntityToPrint) {// isToPrint === false or undefined
            if (this.control.viewsImageBinding) {
                const mapBindingValue = this.getMapBindingPropertyValue();
                if(!!mapBindingValue){
                    this.guidSToDelete.push({ type: ImageType.IMAGE_MAP, guid: mapBindingValue.toString() });
                    this.setDataBindingGuid(null, this.control.viewsImageBinding);
                }
            }
            if (this.control.legendImageBinding) {
                const legendImageBindingValue = this.getlegendImageBindingPropertyValue();
                if(!!legendImageBindingValue){
                    this.guidSToDelete.push({ type: ImageType.IMAGE_LEGEND, guid: legendImageBindingValue.toString() });
                    this.setDataBindingGuid(null, this.control.legendImageBinding);
                }
            }
        } else{ // is sure that isToPrint === true
            if (this.control.viewsImageBinding) {
                const mapBindingValue = this.getMapBindingPropertyValue();
                mapGuid = this.ensureMapGuid(mapBindingValue);
            }
            if (this.control.legendImageBinding) {
                const legendImageBindingValue = this.getlegendImageBindingPropertyValue();
                legendGuid = this.ensureLegendGuid(legendImageBindingValue);
            }
            if (mapGuid) {
                this.guids.push({ type: ImageType.IMAGE_MAP, guid: mapGuid });
            }
            if (legendGuid) {
                this.guids.push({ type: ImageType.IMAGE_LEGEND, guid: legendGuid });
            }
        }
    }

    private setIsToPrint(): void {
        this.isLocalCPEntityToPrint = this.getCurrentIsToPrint();
    }

    private getCurrentIsToPrint(): boolean {
        if (this.control.isToPrintBinding) {
            const isToPrintBindingValue = this.modelService.getValue(this.control.isToPrintBinding);
            return !!isToPrintBindingValue;
        } else if (this.localCpEntity?.parent?.hasProperty('IsToPrint')) {
            return !!this.localCpEntity?.parent?.getPropertyValue('IsToPrint');
        }
        return true;
    }

    private prepareSaveHandler(): ObservableOrAwaitable<EventResult> {
        if (!this.localCpEntity.isModified && this.isLocalCPEntityToPrint == this.getCurrentIsToPrint()) {
            return of(undefined);
        }
        this.beforeSave();
        return this.containerComponent.getImages()
            .then((imageWrapper: RasterImgWrapper[]) => {
                let eventResult: EffectResultImage[];
                eventResult = this.guids.map((item: IGuid) => {
                    const requested: RasterImgWrapper | undefined = imageWrapper.find((rastImgWrap: RasterImgWrapper) => {
                        return rastImgWrap.getImg().type === item.type;
                    });
                    const result: EffectResultImage = {
                        type: 'image',
                        id: item.guid,
                        data: requested!.getImg().base64
                    }

                    return result;
                })
                return eventResult;
            }).then((eventResult: EffectResultImage[]) => {
                if (!!this.guidSToDelete && this.guidSToDelete.length === 0) {
                    return eventResult;
                }
                const resultImgDelete: EffectResultImageDelete[] = this.guidSToDelete.map((itemToDelete: IGuid) => {
                    const result: EffectResultImageDelete = {
                        type: 'image-delete',
                        id: itemToDelete.guid,
                    };
                    return result;
                });
                return [...eventResult, ...resultImgDelete];
            });
    }

    private saveHandlerWrapper(): ObservableOrAwaitable<EventResult> {
        return this.prepareSaveHandler();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.containerSub) {
            this.containerSub.unsubscribe();
        }
        this.cpEffects.off('collectImages', this.saveHandlerWrapper.bind(this));
    }

}

